import { useState } from "react"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { MFA_API_ERROR, useDisableAuthenticatorMutation, useReauthenticatePasswordMutation } from "@api/mfa-api.ts"
import { useNavigate } from "react-router-dom"
import type { RestFetchError } from "@/types/common.ts"

export default function useMfaDisable() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [triggerReAuth, { isLoading: isLoadingReAuth }] = useReauthenticatePasswordMutation()
	const [triggerDisable, { isLoading: isLoadingDisable }] = useDisableAuthenticatorMutation()

	const isLoading = isLoadingDisable || isLoadingReAuth

	const [password, setPassword] = useState("")
	const [otpCode, setOtpCode] = useState("")
	const [isRecoveryMode, setRecoveryMode] = useState(false)
	const [step, setStep] = useState(0)

	async function verifyReAuthPassword() {
		if (password === "") {
			toast.error(t("Salasana on annettava"))
			return
		}
		setPassword("")

		try {
			await triggerReAuth({ password, purpose: "disable_mfa" }).unwrap()
			setStep(1)
		} catch (err) {
			handleFetchError(err as RestFetchError)
		}
	}

	async function disableMfa() {
		if (otpCode === "") {
			toast.error(t("Koodi on annettava"))
			return
		}
		setOtpCode("")

		try {
			await triggerDisable(isRecoveryMode ? { recoveryCode: otpCode } : { code: otpCode }).unwrap()
			navigate("/settings")
		} catch (err) {
			handleFetchError(err as RestFetchError)
		}
	}

	function handleFetchError(err: RestFetchError) {
		const { code } = err.data
		if (code === MFA_API_ERROR.INVALID_CREDENTIALS) {
			toast.error(t("Väärä salasana."))
			return
		}
		if (code === MFA_API_ERROR.MFA_INVALID_CODE) {
			toast.error(t("Väärä koodi."))
			return
		}
		if (code === MFA_API_ERROR.FEATURE_NOT_AVAILABLE) {
			toast.error(t("Ominaisuus ei ole tuettu nykyisellä käyttäjällä."))
			return
		}
		if (code === MFA_API_ERROR.MFA_SESSION_EXPIRED || code === MFA_API_ERROR.RE_AUTH_INVALID) {
			toast.error(t("Uudelleen kirjautuminen on vanhentunut. Aloita käyttöönotto uudelleen."))
			return
		}
		if (code === MFA_API_ERROR.MFA_ALREADY_DISABLED) {
			toast.error(t("Monivaiheinen tunnistautuminen ei ole käytössä."))
			return
		}
		toast.error(t("Jotain meni vikaan."))
	}

	async function onNextStep(): Promise<void> {
		if (step === 0) return await verifyReAuthPassword()
		await disableMfa()
	}

	function getNextStepButtonLabel() {
		if (step === 0) return t("Vahvista")
		return t("Poista käytöstä")
	}

	function toggleRecoveryMode() {
		setRecoveryMode((prev) => !prev)
		setOtpCode("")
	}

	const onPasswordChange = (password: string) => setPassword(password)
	const onOtpCodeChange = (otpCode: string) => setOtpCode(otpCode)

	return {
		setOtpCode,
		isLoading,
		otpCode,
		password,
		setPassword,
		toggleRecoveryMode,
		step,
		onOtpCodeChange,
		onPasswordChange,
		getNextStepButtonLabel,
		onNextStep,
		isRecoveryMode,
	}
}
